/**
 * Lightweight SEO helper for the PayGBot React SPA.
 *
 * Sets document.title, the standard meta description, Open Graph,
 * Twitter card, canonical URL, and optional JSON-LD structured data
 * for the current page — without pulling in react-helmet.
 *
 * Modern Google + AI search crawlers (ChatGPT, Perplexity, Gemini,
 * Claude Citations, Bing) execute JS and respect tags set after
 * hydration. This helper updates the <head> on mount and cleans up
 * the dynamic JSON-LD blocks on unmount so route changes don't leak
 * schema between pages.
 */
import { useEffect } from 'react';

const SITE_URL = 'https://paygbot.com';
const DEFAULT_OG_IMAGE = 'https://paygbot.com/icon-512.png';

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function injectJsonLd(id, payload) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

/**
 * usePageSEO(config)
 *
 * config = {
 *   title:        '...page title shown in browser tab and search results',
 *   description:  '...meta description (≈155 chars)',
 *   path:         '/route' — used to build canonical + og:url
 *   ogImage?:     full URL (optional)
 *   ogType?:      'website' | 'article' (default: 'website')
 *   jsonLd?:      array of structured data objects (Organization, FAQ, Article…)
 *   keywords?:    string of comma-separated keywords (used for AI-search hints)
 * }
 */
export function usePageSEO({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd = [],
  keywords,
  publishedDate,
  modifiedDate,
  author = 'Vickie Mcglawn',
}) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const prevTitle = document.title;
    if (title) document.title = title;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('author', author);
    setCanonical(url);

    // Open Graph
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', url, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', 'PayGBot', true);

    // Twitter / X
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Article-specific tags
    if (ogType === 'article') {
      if (publishedDate) setMeta('article:published_time', publishedDate, true);
      if (modifiedDate) setMeta('article:modified_time', modifiedDate, true);
      setMeta('article:author', author, true);
    }

    // Inject any JSON-LD blocks (Organization, FAQ, Article, etc.)
    const injectedIds = [];
    jsonLd.forEach((blob, idx) => {
      const id = `paygbot-jsonld-${path.replace(/[^a-z0-9]/gi, '_')}-${idx}`;
      injectJsonLd(id, blob);
      injectedIds.push(id);
    });

    return () => {
      // Restore tab title on unmount so stale titles don't flash.
      if (title) document.title = prevTitle;
      // Remove only THIS page's JSON-LD; leave global Organization etc alone.
      injectedIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, ogImage, ogType, keywords, publishedDate, modifiedDate, author]);
}

export const PAYGBOT_SITE_URL = SITE_URL;
